// src/services/nftFetcher/contractMetadataFetcher.js
import { ethers } from 'ethers';
import secureFetch from '../../utils/secureFetch';
import { supportedChains } from '../../chains';

// Configuration
const ENABLE_METADATA_FETCHING = true;
const CONTRACT_CALL_DELAY = 50; // ms between contract calls
const METADATA_FETCH_TIMEOUT = 10000; // 10 seconds for IPFS/metadata requests

// ERC-721 ABI for tokenURI function
const ERC721_ABI = [
  "function tokenURI(uint256 tokenId) external view returns (string memory)"
];

/**
 * Simple delay function for rate limiting
 * @param {number} ms - Milliseconds to delay
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Clean and normalize metadata URIs (handle IPFS, etc.)
 * @param {string} uri - Raw URI from contract
 * @returns {string} - Cleaned URI
 */
const cleanMetadataUri = (uri) => {
  if (!uri) return null;
  
  // Handle IPFS URIs
  if (uri.startsWith('ipfs://')) {
    return uri.replace('ipfs://', 'https://ipfs.io/ipfs/');
  }
  
  // Handle relative IPFS hashes (starting with Qm...)
  if (uri.match(/^Qm[1-9A-HJ-NP-Za-km-z]{44}$/)) {
    return `https://ipfs.io/ipfs/${uri}`;
  }
  
  // Handle other IPFS patterns
  if (uri.match(/^[a-zA-Z0-9]{46,}$/)) {
    return `https://ipfs.io/ipfs/${uri}`;
  }
  
  // Handle data URIs (base64 encoded JSON)
  if (uri.startsWith('data:application/json;base64,')) {
    try {
      const base64Data = uri.replace('data:application/json;base64,', '');
      const jsonString = atob(base64Data);
      return JSON.parse(jsonString);
    } catch (error) {
      console.warn('Failed to parse base64 data URI:', error);
      return null;
    }
  }
  
  // Return as-is for HTTP/HTTPS URLs
  return uri;
};

/**
 * Fetch metadata JSON from URI
 * @param {string} uri - Metadata URI
 * @returns {Promise<Object|null>} - Metadata object or null
 */
const fetchMetadataFromUri = async (uri) => {
  try {
    const cleanUri = cleanMetadataUri(uri);
    if (!cleanUri) return null;
    
    // If cleanUri is already an object (from data URI), return it
    if (typeof cleanUri === 'object') {
      return cleanUri;
    }
    
    const response = await secureFetch(cleanUri, {
      headers: {
        'Accept': 'application/json',
      },
      // Add timeout for IPFS requests which can be slow
      signal: AbortSignal.timeout(METADATA_FETCH_TIMEOUT)
    });
    
    if (!response.ok) {
      throw new Error(`Metadata fetch error: ${response.status}`);
    }
    
    const metadata = await response.json();
    return metadata;
  } catch (error) {
    console.warn(`Could not fetch metadata from URI: ${uri}`, error);
    return null;
  }
};

/**
 * Get RPC provider for a specific chain using the existing chain configuration
 * @param {string} chainKey - The chain identifier
 * @returns {ethers.JsonRpcProvider|null} - Provider instance or null
 */
const getChainProvider = (chainKey) => {
  try {
    const chainConfig = supportedChains[chainKey];
    if (!chainConfig || !chainConfig.rpcUrl) {
      console.warn(`No RPC URL configured for chain: ${chainKey}`);
      return null;
    }
    
    return new ethers.JsonRpcProvider(chainConfig.rpcUrl);
  } catch (error) {
    console.warn(`Could not create provider for chain ${chainKey}:`, error);
    return null;
  }
};

/**
 * Fetch NFT metadata directly from contract using tokenURI
 * @param {string} contractAddress - The NFT contract address
 * @param {string} tokenId - The token ID
 * @param {string} chainKey - The chain identifier
 * @returns {Promise<Object|null>} - Metadata object or null
 */
export const fetchContractMetadata = async (contractAddress, tokenId, chainKey) => {
  try {
    if (!ENABLE_METADATA_FETCHING) {
      return null;
    }
    
    // Add delay between contract calls
    await delay(CONTRACT_CALL_DELAY);
    
    // Get provider for the chain using existing chain configuration
    const provider = getChainProvider(chainKey);
    if (!provider) {
      return null;
    }
    
    // Create contract instance
    const contract = new ethers.Contract(contractAddress, ERC721_ABI, provider);
    
    // Get token URI from contract
    const tokenUri = await contract.tokenURI(tokenId);
    
    if (!tokenUri) {
      console.warn(`No tokenURI found for ${contractAddress}:${tokenId} on ${chainKey}`);
      return null;
    }
    
    console.log(`Fetching metadata for ${chainKey} NFT ${contractAddress}:${tokenId} from URI: ${tokenUri}`);
    
    // Fetch metadata from the URI
    const metadata = await fetchMetadataFromUri(tokenUri);
    
    if (metadata) {
      // Add the tokenURI to metadata for reference
      metadata.tokenUri = tokenUri;
      metadata.chainKey = chainKey;
    }
    
    return metadata;
  } catch (error) {
    console.warn(`Could not fetch contract metadata for ${contractAddress}:${tokenId} on ${chainKey}:`, error);
    return null;
  }
};

/**
 * Enable or disable metadata fetching globally
 * @param {boolean} enabled - Whether to enable metadata fetching
 */
export const setMetadataFetchingEnabled = (enabled) => {
  ENABLE_METADATA_FETCHING = enabled;
};

/**
 * Get all configured chains that support metadata fetching
 * @returns {Array<string>} - Array of chain keys that have RPC endpoints
 */
export const getConfiguredChains = () => {
  return Object.keys(supportedChains).filter(chainKey => {
    const chainConfig = supportedChains[chainKey];
    return chainConfig && chainConfig.rpcUrl;
  });
};